'use client'
import React, { useRef, useState } from 'react'
import ClassNames from 'classnames'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import Event from 'Utilities/Event'
import PanelContext from 'Contexts/PanelContext'
import ColorFinder from 'Utilities/ColorFinder'
import LayerColorFinder from 'Utilities/LayerColorFinder'
import Typography from 'Atoms/Typography'
import InlineSpinner from 'Atoms/InlineSpinner'
import Svg from 'Atoms/Svg'
import Brands from 'Constants/Brand'

import useGoogleAnalytics from 'Hooks/useGoogleAnalytics'
import useSegment from 'Hooks/useSegment'

import styles from './Button.module.scss'
import useNavigationState from '../../Hooks/useAppInstall/useNavigationState'


const propTypes = {
    className: PropTypes.string,
    intent: PropTypes.oneOf([
        'positive',
        'negative',
        'action',
        'warning',
        'subdued',
        undefined
    ]),
    brand: PropTypes.oneOf(Brands),
    size: PropTypes.oneOf(['large', 'medium', 'small', undefined]),
    type: PropTypes.oneOf(['submit', 'input', 'button', undefined]),
    href: PropTypes.string,
    onClick: PropTypes.func,
    invert: PropTypes.bool,
    inline: PropTypes.bool,
    fill: PropTypes.bool,
    allowWrap: PropTypes.bool,
    ariaLabel: PropTypes.string,
    event:
        PropTypes.shape({
            optOut: PropTypes.bool,
            //top level faceting in GA/Segment. Adding a new category is okay to do,
            //just make sure that people reading the tracked events know what the new cateogry means
            //reflect changes in ListItemLink.js
            category: PropTypes.oneOf(['Header', 'Footer', 'Email', 'BuildList', 'Pdp', 'Prp', 'Development', 'HomePage', 'Error Page', undefined]),
            action: PropTypes.string,
            label: PropTypes.string
        }).isRequired,
    text: PropTypes.string,
    icon: PropTypes.string
}

const Button = ({
    className,
    intent,
    brand,
    size,
    type,
    href,
    onClick,
    invert,
    inline,
    fill,
    allowWrap = true,
    ariaLabel,
    event,
    children,
    testingName,
    text,
    icon,
    isWaiting,
    disabled,
    gaEvent,
    segmentEvent,
    ...props
}) => {

    const ref = useRef()
    const { sendGoogleAnalyticsEvent } = useGoogleAnalytics()
    const { sendSegmentTrackEvent } = useSegment()
    const { showNavigation } = useNavigationState();
    const onClickWrapper = (e, onClick, event, gaEvent, segmentEvent, disabled) => {
        if (disabled) {
            return
        }
        if (href) {
            showNavigation();
        }

        if (event && !gaEvent && !segmentEvent) {
            let buttonEvent = {
                category: event.category,
                action: `${event.action}`,
                label: event.label
            }
            Event(buttonEvent)()
        }

        if (gaEvent) {
            gaEvent.nonInteraction = false
            sendGoogleAnalyticsEvent(gaEvent)
        }

        if (segmentEvent) {
            sendSegmentTrackEvent(segmentEvent)
        }

        if (onClick) {
            onClick(e)
        }
    }

    if (!intent && !brand) return false
    if (!event && !gaEvent && !segmentEvent) return false
    if (!disabled && !(onClick || href || type === 'submit')) {
        console.info('Button does not have onClick or href, but is rendered', ref)
        console.error('Button does not have onClick or href, but is rendered', ref?.current?.className, ref?.current?.id)
    } 
    intent = intent && intent.toLowerCase()
    brand = brand && brand.toLowerCase()
    size = (size && size.toLowerCase()) || 'large'


    let { color, hoverColor, panelColor } = ColorFinder(
        intent,
        undefined,
        brand,
        disabled
    )


    const component = href ? 'a' : type === 'submit' ? 'button' : 'button'

    const panelContextValue = {
        brand: brand,
        intent: intent,
        invert: invert
    }
    const globalClassNames = ClassNames(className, invert ? `btn btn-invert` : 'btn', `bg-color-${brand ?? intent}`)
    const buttonNoChildrenClasses = ClassNames(
        className,
        styles.button,
        size === 'large' && styles.buttonLarge,
        size === 'small' && styles.buttonSmall,
        !allowWrap && styles.noWrap,
        isWaiting && styles.buttonWaiting,
        fill && styles.buttonFill,
        disabled && styles.buttonDisabled,
        globalClassNames
    )

    if (intent === "subdued" && !brand && !disabled) {
        //same as optionButton styling
        color = LayerColorFinder(null, null, null, null)
    }

    return (
        <PanelContext.Provider value={panelContextValue}>
            <StyledButton
                as={component}
                className={buttonNoChildrenClasses}
                type={type == 'submit' || type == 'button' ? type : null}
                href={href}
                onClick={(e) => onClickWrapper(e, onClick, event, gaEvent, segmentEvent, disabled)}
                aria-label={ariaLabel}
                color={color}
                hoverColor={hoverColor}
                invert={invert}
                panelColor={panelColor}
                data-testid={testingName}
                disabled={disabled || isWaiting}
                isWaiting={isWaiting}
                intent={intent}
                ref={ref}
                {...props}
            >
                {isWaiting ?
                    <>
                        <div className={styles.spinnerWrapper}>
                            <InlineSpinner intent={intent} brand={panelContextValue.brand} />
                        </div>

                    </>
                    :
                    null
                }
                <ButtonInnerContent text={text} icon={icon} forwardedChildren={children} size={size} disabled={disabled} />

            </StyledButton>
        </PanelContext.Provider>
    )
}

const ButtonInnerContent = ({ forwardedChildren, icon, text, size, disabled }) => {
    /*
        IMPORTANT
        If you use children just assume that none of the attributes are working according to the tested stae and you must test all functionality
    */
    if (forwardedChildren) {
        return forwardedChildren
    }

    else {
        return (
            <>
                {icon ? (
                    <div style={{
                        height: size === "small" ? "1.5rem" : "2.25rem",
                        display: 'flex',
                        alignItems: 'center',
                        marginRight: text ? size === "small" ? "0.5rem" : "1rem" : "0"
                    }}>
                        <Svg icon={icon} size={size === 'small' ? 1 : 1.5} tone={'normal'} disabled={disabled} />
                    </div>
                ) : (
                    false
                )}
                {text ? (
                    <Typography
                        tone={'contrast'}
                        font={'bold'}
                        size={size === 'small' ? 1 : 1.5}

                        inline
                        disabled={disabled}
                    >
                        {text}
                    </Typography>
                ) : (
                    false
                )}
            </>
        )
    }
}

const StyledButton = styled.button`
    background-color: ${(props) => props.invert ? props.panelColor : props.color};
    border: ${(props) => props.invert ? '2px solid ' + props.color :
        props.intent === 'subdued' ? '1px solid ' + LayerColorFinder(3) : '2px solid transparent'};

    ${(props) => !props.disabled && !props.isWaiting ?
        `&:hover {
            background-color: ${props.invert ? props.color : props.hoverColor};    
            ${props.invert ?
            `p{
                    color: ${props.panelColor};
                }
                svg{
                    fill: ${props.panelColor};
                }`
            : ''}        
        }`
        : ''
    }
`



Button.propTypes = propTypes

export default Button
